import React, { useState } from 'react';
import { Modal, View, StyleSheet, TouchableOpacity } from 'react-native';
import { Text, useThemeName } from '@tamagui/core';
import { YStack, XStack } from '@tamagui/stacks';
import { X, Clock, Check } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import { COLORS } from '@/constants/colors';

type ReminderOption = {
  id: string;
  label: string;
  value: string;
};

type ReminderModalProps = {
  visible: boolean;
  onClose: () => void;
  onSave: (reminders: string[]) => void;
  meetingTitle?: string;
  meetingDate?: string;
};

export const ReminderModal: React.FC<ReminderModalProps> = ({
  visible,
  onClose,
  onSave,
  meetingTitle,
  meetingDate,
}) => {
  const { t } = useTranslation();
  const themeName = useThemeName();
  const isDark = themeName === 'dark';
  
  const titleColor = isDark ? '#ffffff' : '#18181b';
  const subtitleColor = isDark ? '#a1a1aa' : '#71717a';
  const bgColor = isDark ? '#1C1C1C' : COLORS.WHITE;
  const borderColor = isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.08)';

  const reminderOptions: ReminderOption[] = [
    { id: '24h', label: t('screens.myApplications.reminder24h', '24h przed spotkaniem'), value: '24h' },
    { id: '2h', label: t('screens.myApplications.reminder2h', '2h przed spotkaniem'), value: '2h' },
    { id: '30min', label: t('screens.myApplications.reminder30min', '30 min przed spotkaniem'), value: '30min' },
  ];

  const [selectedReminders, setSelectedReminders] = useState<string[]>(['24h', '2h']);

  const toggleReminder = (id: string) => {
    setSelectedReminders(prev => 
      prev.includes(id) 
        ? prev.filter(r => r !== id)
        : [...prev, id]
    );
  };

  const handleSave = () => {
    onSave(selectedReminders);
    onClose();
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <YStack
          backgroundColor={bgColor}
          borderRadius={20}
          padding="$5"
          width="90%"
          maxWidth={400}
          space="$4"
        >
          {/* Header */}
          <XStack justifyContent="space-between" alignItems="center">
            <Text fontSize={18} fontWeight="600" fontFamily="Poppins" color={titleColor}>
              {t('screens.myApplications.setReminder', 'Ustaw przypomnienie')}
            </Text>
            <TouchableOpacity onPress={onClose}>
              <X size={24} color={subtitleColor} />
            </TouchableOpacity>
          </XStack>

          {/* Meeting Info */}
          {meetingTitle && (
            <YStack
              backgroundColor={isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'}
              padding="$3"
              borderRadius={12}
            >
              <Text fontSize={14} fontWeight="500" fontFamily="Poppins" color={titleColor}>
                {meetingTitle}
              </Text>
              {meetingDate && (
                <Text fontSize={12} fontFamily="Poppins" color={subtitleColor} marginTop="$1">
                  {meetingDate}
                </Text>
              )}
            </YStack>
          )}

          {/* Reminder Options */}
          <YStack space="$2">
            <Text fontSize={14} fontFamily="Poppins" color={subtitleColor} marginBottom="$1">
              {t('screens.myApplications.chooseReminders', 'Wybierz przypomnienia:')}
            </Text>
            
            {reminderOptions.map((option) => {
              const isSelected = selectedReminders.includes(option.id);
              return (
                <TouchableOpacity key={option.id} onPress={() => toggleReminder(option.id)}>
                  <XStack
                    backgroundColor={isSelected ? 'rgba(94, 128, 156, 0.15)' : 'transparent'}
                    borderWidth={1}
                    borderColor={isSelected ? COLORS.PRIMARY : borderColor}
                    borderRadius={12}
                    padding="$3"
                    alignItems="center"
                    space="$3"
                  >
                    <XStack
                      width={24}
                      height={24}
                      borderRadius={12}
                      backgroundColor={isSelected ? COLORS.PRIMARY : 'transparent'}
                      borderWidth={isSelected ? 0 : 2}
                      borderColor={borderColor}
                      alignItems="center"
                      justifyContent="center"
                    >
                      {isSelected && <Check size={14} color="white" />}
                    </XStack>
                    <Clock size={18} color={isSelected ? COLORS.PRIMARY : subtitleColor} />
                    <Text 
                      fontSize={14} 
                      fontFamily="Poppins" 
                      color={isSelected ? titleColor : subtitleColor}
                      flex={1}
                    >
                      {option.label}
                    </Text>
                  </XStack>
                </TouchableOpacity>
              );
            })}
          </YStack>

          {/* Save Button */}
          <TouchableOpacity onPress={handleSave}>
            <YStack
              backgroundColor={COLORS.PRIMARY}
              borderRadius={12}
              paddingVertical="$3"
              alignItems="center"
            >
              <Text fontSize={16} fontWeight="600" fontFamily="Poppins" color="white">
                {t('screens.myApplications.saveReminder', 'Zapisz przypomnienie')}
              </Text>
            </YStack>
          </TouchableOpacity>

          {/* TODO: API - Endpoint needed: PATCH /api/meeting-applications/{id}/reminder
              Request body: { reminderSettings: string[] }
              Example: { reminderSettings: ["24h", "2h", "30min"] }
              
              MeetingAnnouncementApplicationView should include:
              reminderSettings: string[] | null - list of selected reminder times
          */}
        </YStack>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
});


